import Code from '@/components/dashboard/Code';
import SectionActions from '@/docs/SectionActions';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function PythonSDKDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="AxionQuant Python SDK" description="The AxionQuant Python SDK (also called the Axion SDK) provides a comprehensive wrapper for interacting with the AxionQuant Financial API.
          This SDK simplifies access to financial data including ESG scores, stock prices, crypto..."  canonicalUrl="https://axionquant.com/docs/client-libraries/python-SDK/01-core" />
      <div className="mb-10">
        <DocHeading className="text-3xl font-bold text-gray-900 mb-4">AxionQuant Python SDK</DocHeading>
        <p className="text-lg text-gray-700 max-w-[900px]">
          The AxionQuant Python SDK (also called the Axion SDK) provides a comprehensive wrapper for interacting with the AxionQuant Financial API.
          This SDK simplifies access to financial data including ESG scores, stock prices, cryptocurrency data,
          economic indicators, company financials, insider trading, SEC filings, and more. All methods return
          normalized Python data structures with proper type coercion.
        </p>
      </div>

      <div className="space-y-12" ref={sectionRef}>
        {/* ---------- Installation ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Installation</h2>
            <p className="text-gray-700">
              Install the SDK via pip, or clone the repository for development.
            </p>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="pip / GitHub" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">pip / GitHub</p>
            <Code lang="bash">{`# Install via pip
pip install axionquant-sdk

# Or clone from GitHub
git clone https://github.com/axionquant/python-sdk.git
cd python-sdk
pip install -e .`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Quick Start ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Quick Start</h2>
            <p className="text-gray-700">
              Initialize the client and start fetching data. All methods return normalized Python objects.
            </p>
          </div>
          <div className="border-l-4 border-blue-500 pl-4 mb-3">
            <p className="font-mono text-sm text-gray-800 mb-1">Steps</p>
            <ol className="text-sm text-gray-600 list-decimal list-inside space-y-1">
              <li>Import the <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">Axion</code> class from <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">axion</code></li>
              <li>Create a client with your API key</li>
              <li>Call methods on category attributes (e.g., <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">client.stocks.quote("AAPL")</code>)</li>
              <li>Use the returned dict/list directly (numbers, booleans are already converted)</li>
            </ol>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Quick Start (Python)" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Quick Start (Python)</p>
            <Code lang="python">{`from axion import Axion

# Initialize client
client = Axion(api_key="your_api_key_here")

# Get stock data
quote = client.stocks.quote("AAPL")
print(f"Apple price: {quote['price']}")

# Get ESG data
esg = client.esg.data("AAPL")
print(f"ESG Score: {esg['score']}")

# Get company profile
profile = client.profiles.info("AAPL")
print(f"Company: {profile['name']}")`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Client Initialization ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Client Initialization</h2>
            <p className="text-gray-700 mb-6">
              The <code className="text-sm bg-gray-100 px-1.5 py-0.5 rounded">Axion</code> client requires an API key for authentication.
               The SDK is organized into 18 specialized API classes, accessible as attributes of the main client.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Constructor Parameters</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">api_key</td>
                    <td className="px-4 py-3 text-sm text-gray-700">str</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                        Optional*
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      Your AxionQuant API key. If omitted, it must be provided in the <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">Authorization</code> header per request, but most endpoints require authentication.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Initialization example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Initialization example</p>
            <Code lang="python">{`from axion import Axion

# Recommended: set API key once
client = Axion(api_key="your_api_key_here")

# All API categories are now available
credit = client.credit.search("Apple")
esg = client.esg.data("AAPL")
stocks = client.stocks.quote("AAPL")
crypto = client.crypto.quote("BTC-USD")`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- API Categories Overview ---------- */}
      <div>
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">API Categories</h2>
          <p className="text-gray-700 max-w-[900px] mb-6">
            The SDK is organized into 18 specialized API classes, each handling a specific data domain.
            All API classes are accessible as attributes of the main client.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-[1400px]">
          <div className="bg-blue-50 p-5 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-900 mb-2">client.credit</h4>
            <p className="text-sm text-blue-800 mb-2">Credit ratings & entity search</p>
            <code className="text-xs text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">credit.search()</code>
            <code className="text-xs text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">credit.ratings()</code>
          </div>
          <div className="bg-green-50 p-5 rounded-lg border border-green-200">
            <h4 className="font-semibold text-green-900 mb-2">client.esg</h4>
            <p className="text-sm text-green-800 mb-2">Environmental, Social, Governance</p>
            <code className="text-xs text-green-700 bg-green-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">esg.data()</code>
          </div>
          <div className="bg-purple-50 p-5 rounded-lg border border-purple-200">
            <h4 className="font-semibold text-purple-900 mb-2">client.etfs</h4>
            <p className="text-sm text-purple-800 mb-2">ETF fund data, holdings, exposure</p>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.tickers()</code>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.ticker()</code>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.prices()</code>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.fund()</code>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.holdings()</code>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.holdings_all()</code>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.exposure()</code>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.weights()</code>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.gainers()</code>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.losers()</code>
            <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">etfs.quote()</code>
          </div>
          <div className="bg-yellow-50 p-5 rounded-lg border border-yellow-200">
            <h4 className="font-semibold text-yellow-900 mb-2">client.supply_chain</h4>
            <p className="text-sm text-yellow-800 mb-2">Customers, peers, suppliers</p>
            <code className="text-xs text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">supply_chain.customers()</code>
            <code className="text-xs text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">supply_chain.peers()</code>
            <code className="text-xs text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">supply_chain.suppliers()</code>
          </div>
          <div className="bg-red-50 p-5 rounded-lg border border-red-200">
            <h4 className="font-semibold text-red-900 mb-2">client.stocks</h4>
            <p className="text-sm text-red-800 mb-2">Stock quotes, prices, tickers</p>
            <code className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">stocks.tickers()</code>
            <code className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">stocks.ticker()</code>
            <code className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">stocks.quote()</code>
            <code className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">stocks.prices()</code>
            <code className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">stocks.gainers()</code>
            <code className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">stocks.losers()</code>
          </div>
          <div className="bg-indigo-50 p-5 rounded-lg border border-indigo-200">
            <h4 className="font-semibold text-indigo-900 mb-2">client.crypto</h4>
            <p className="text-sm text-indigo-800 mb-2">Cryptocurrency data</p>
            <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">crypto.tickers()</code>
            <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">crypto.ticker()</code>
            <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">crypto.quote()</code>
            <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">crypto.prices()</code>
            <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">crypto.gainers()</code>
            <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">crypto.losers()</code>
          </div>
          <div className="bg-pink-50 p-5 rounded-lg border border-pink-200">
            <h4 className="font-semibold text-pink-900 mb-2">client.forex</h4>
            <p className="text-sm text-pink-800 mb-2">Foreign exchange currency data</p>
            <code className="text-xs text-pink-700 bg-pink-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">forex.tickers()</code>
            <code className="text-xs text-pink-700 bg-pink-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">forex.ticker()</code>
            <code className="text-xs text-pink-700 bg-pink-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">forex.quote()</code>
            <code className="text-xs text-pink-700 bg-pink-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">forex.prices()</code>
            <code className="text-xs text-pink-700 bg-pink-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">forex.gainers()</code>
            <code className="text-xs text-pink-700 bg-pink-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">forex.losers()</code>
          </div>
          <div className="bg-amber-50 p-5 rounded-lg border border-amber-200">
            <h4 className="font-semibold text-amber-900 mb-2">client.futures</h4>
            <p className="text-sm text-amber-800 mb-2">Commodity & financial futures</p>
            <code className="text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">futures.tickers()</code>
            <code className="text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">futures.ticker()</code>
            <code className="text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">futures.quote()</code>
            <code className="text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">futures.prices()</code>
            <code className="text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">futures.gainers()</code>
            <code className="text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">futures.losers()</code>
          </div>
          <div className="bg-teal-50 p-5 rounded-lg border border-teal-200">
            <h4 className="font-semibold text-teal-900 mb-2">client.indices</h4>
            <p className="text-sm text-teal-800 mb-2">Stock market indices</p>
            <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">indices.tickers()</code>
            <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">indices.ticker()</code>
            <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">indices.quote()</code>
            <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">indices.prices()</code>
            <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">indices.gainers()</code>
            <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">indices.losers()</code>
            <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">indices.components()</code>
            <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">indices.exposure()</code>
          </div>
          <div className="bg-cyan-50 p-5 rounded-lg border border-cyan-200">
            <h4 className="font-semibold text-cyan-900 mb-2">client.econ</h4>
            <p className="text-sm text-cyan-800 mb-2">Economic indicators & calendar</p>
            <code className="text-xs text-cyan-700 bg-cyan-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">econ.find()</code>
            <code className="text-xs text-cyan-700 bg-cyan-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">econ.search()</code>
            <code className="text-xs text-cyan-700 bg-cyan-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">econ.dataset()</code>
            <code className="text-xs text-cyan-700 bg-cyan-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">econ.calendar()</code>
          </div>
          <div className="bg-orange-50 p-5 rounded-lg border border-orange-200">
            <h4 className="font-semibold text-orange-900 mb-2">client.news</h4>
            <p className="text-sm text-orange-800 mb-2">Financial news articles</p>
            <code className="text-xs text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">news.general()</code>
            <code className="text-xs text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">news.company()</code>
            <code className="text-xs text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">news.country()</code>
            <code className="text-xs text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">news.category()</code>
          </div>
          <div className="bg-lime-50 p-5 rounded-lg border border-lime-200">
            <h4 className="font-semibold text-lime-900 mb-2">client.sentiment</h4>
            <p className="text-sm text-lime-800 mb-2">News & social sentiment</p>
            <code className="text-xs text-lime-700 bg-lime-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">sentiment.social()</code>
            <code className="text-xs text-lime-700 bg-lime-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">sentiment.news()</code>
            <code className="text-xs text-lime-700 bg-lime-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">sentiment.analyst()</code>
          </div>
          <div className="bg-violet-50 p-5 rounded-lg border border-violet-200">
            <h4 className="font-semibold text-violet-900 mb-2">client.profiles</h4>
            <p className="text-sm text-violet-800 mb-2">Company profiles & summaries</p>
            <code className="text-xs text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">profiles.profile()</code>
            <code className="text-xs text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">profiles.info()</code>
            <code className="text-xs text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">profiles.statistics()</code>
            <code className="text-xs text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">profiles.summary()</code>
            <code className="text-xs text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">profiles.calendar()</code>
            <code className="text-xs text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">profiles.recommendation()</code>
          </div>
          <div className="bg-rose-50 p-5 rounded-lg border border-rose-200">
            <h4 className="font-semibold text-rose-900 mb-2">client.earnings</h4>
            <p className="text-sm text-rose-800 mb-2">Earnings data & estimates</p>
            <code className="text-xs text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">earnings.history()</code>
            <code className="text-xs text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">earnings.trend()</code>
            <code className="text-xs text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">earnings.index()</code>
            <code className="text-xs text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">earnings.report()</code>
            <code className="text-xs text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">earnings.transcript()</code>
            <code className="text-xs text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">earnings.transcript_sentiment()</code>
          </div>
          <div className="bg-emerald-50 p-5 rounded-lg border border-emerald-200">
            <h4 className="font-semibold text-emerald-900 mb-2">client.filings</h4>
            <p className="text-sm text-emerald-800 mb-2">SEC filings data</p>
            <code className="text-xs text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">filings.recent()</code>
            <code className="text-xs text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">filings.history()</code>
            <code className="text-xs text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">filings.list_forms()</code>
            <code className="text-xs text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">filings.search()</code>
            <code className="text-xs text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">filings.document_sentiment()</code>
            <code className="text-xs text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">filings.document_text()</code>
          </div>
          <div className="bg-sky-50 p-5 rounded-lg border border-sky-200">
            <h4 className="font-semibold text-sky-900 mb-2">client.financials</h4>
            <p className="text-sm text-sky-800 mb-2">Financial statements & metrics</p>
            <code className="text-xs text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.revenue()</code>
            <code className="text-xs text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.metrics()</code>
            <code className="text-xs text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.snapshot()</code>
            <code className="text-xs text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.balance_sheet()</code>
            <code className="text-xs text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.income_statement()</code>
            <code className="text-xs text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.cash_flow_statement()</code>
            <code className="text-xs text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.dcf_value()</code>
            <code className="text-xs text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.dcf_rate()</code>
            <code className="text-xs text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.eps()</code>
            <code className="text-xs text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.pe()</code>
            <code className="text-xs text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.market_cap()</code>
            <code className="text-xs text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.roe()</code>
            <code className="text-xs text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.enterprise_value()</code>
            <code className="text-xs text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.ebitda()</code>
            <code className="text-xs text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">financials.debt_to_equity()</code>
            <span className="text-xs text-sky-600 block mt-1">+24 more financial metrics</span>
          </div>
          <div className="bg-fuchsia-50 p-5 rounded-lg border border-fuchsia-200">
            <h4 className="font-semibold text-fuchsia-900 mb-2">client.insiders</h4>
            <p className="text-sm text-fuchsia-800 mb-2">Insider trading data</p>
            <code className="text-xs text-fuchsia-700 bg-fuchsia-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">insiders.funds()</code>
            <code className="text-xs text-fuchsia-700 bg-fuchsia-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">insiders.individuals()</code>
            <code className="text-xs text-fuchsia-700 bg-fuchsia-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">insiders.institutions()</code>
            <code className="text-xs text-fuchsia-700 bg-fuchsia-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">insiders.ownership()</code>
            <code className="text-xs text-fuchsia-700 bg-fuchsia-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">insiders.activity()</code>
            <code className="text-xs text-fuchsia-700 bg-fuchsia-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">insiders.transactions()</code>
          </div>
          <div className="bg-stone-50 p-5 rounded-lg border border-stone-200">
            <h4 className="font-semibold text-stone-900 mb-2">client.web_traffic</h4>
            <p className="text-sm text-stone-800 mb-2">Website traffic analytics</p>
            <code className="text-xs text-stone-700 bg-stone-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">web_traffic.traffic()</code>
          </div>
        </div>
      </div>

      {/* ---------- Stocks API (Detailed) ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Stocks API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.stocks
            </code>
            <p className="text-gray-700 mb-6">
              Methods for accessing stock market data including tickers, quotes, and historical prices.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">tickers(country, exchange)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get all stock tickers with optional filtering</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">country: str = None</code>, <code className="text-xs bg-gray-100 px-2 py-1 rounded">exchange: str = None</code>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get a single stock ticker by its symbol</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">ticker: str</code>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">quote(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get current quote for a stock</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">ticker: str</code>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">prices(ticker, from_date, to_date, frame)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get historical stock prices</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">ticker: str</code>, <code className="text-xs bg-gray-100 px-2 py-1 rounded">from_date: str = None</code>, <code className="text-xs bg-gray-100 px-2 py-1 rounded">to_date: str = None</code>, <code className="text-xs bg-gray-100 px-2 py-1 rounded">frame: str = 'daily'</code>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">gainers(days, limit, market)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get top stock gainers</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">days: int = None</code>, <code className="text-xs bg-gray-100 px-2 py-1 rounded">limit: int = None</code>, <code className="text-xs bg-gray-100 px-2 py-1 rounded">market: str = None</code>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">losers(days, limit, market)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get top stock losers</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">days: int = None</code>, <code className="text-xs bg-gray-100 px-2 py-1 rounded">limit: int = None</code>, <code className="text-xs bg-gray-100 px-2 py-1 rounded">market: str = None</code>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Stocks API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Stocks API - Example</p>
            <Code lang="python">{`# Get all US stock tickers
us_stocks = client.stocks.tickers(country="US")

# Get quote for a single stock
aapl = client.stocks.quote("AAPL")
print(f"Apple: {aapl['price']} ({aapl['change']}%)")

# Get historical prices with date range
prices = client.stocks.prices(
    "MSFT",
    from_date="2024-01-01",
    to_date="2024-03-31",
    frame="weekly"
)

# Convert to pandas DataFrame
import pandas as pd
df = pd.DataFrame(prices)
df['date'] = pd.to_datetime(df['date'])
print(df.head())`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Crypto API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Cryptocurrency API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.crypto
            </code>
            <p className="text-gray-700 mb-6">
              Methods for accessing cryptocurrency data including tickers, quotes, and historical prices.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">tickers(type)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get all cryptocurrency tickers with optional filtering by type</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">type: str = None</code>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get a single cryptocurrency ticker by its symbol</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">ticker: str</code>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">quote(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get current quote for a cryptocurrency</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">ticker: str</code>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">prices(ticker, from_date, to_date, frame)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get historical prices for a cryptocurrency</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">ticker: str</code>, <code className="text-xs bg-gray-100 px-2 py-1 rounded">from_date: str = None</code>, <code className="text-xs bg-gray-100 px-2 py-1 rounded">to_date: str = None</code>, <code className="text-xs bg-gray-100 px-2 py-1 rounded">frame: str = 'daily'</code>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">gainers(days, limit)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get top crypto gainers</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">days: int = None</code>, <code className="text-xs bg-gray-100 px-2 py-1 rounded">limit: int = None</code>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">losers(days, limit)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get top crypto losers</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">days: int = None</code>, <code className="text-xs bg-gray-100 px-2 py-1 rounded">limit: int = None</code>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Crypto API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Crypto API - Example</p>
            <Code lang="python">{`# Get all crypto tickers
all_crypto = client.crypto.tickers()

# Filter by type (e.g., "coin", "token")
stablecoins = client.crypto.tickers(type="stablecoin")

# Get Bitcoin quote
btc = client.crypto.quote("BTC-USD")
print(f"Bitcoin: {btc['price']:,.2f}")

# Get Ethereum historical prices
eth_prices = client.crypto.prices(
    "ETH-USD",
    from_date="2024-01-01",
    to_date="2024-03-31",
    frame="daily"
)`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Profiles API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Profiles API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.profiles
            </code>
            <p className="text-gray-700 mb-6">
              Comprehensive company profiles, business summaries, and market data.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">profile(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get asset profile and business summary</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">info(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get company profile information</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">statistics(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get key statistics and financial ratios</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">summary(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get summary detail including prices and volumes</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">calendar(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get calendar events including earnings and dividends</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">recommendation(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get analyst recommendation trends</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Profiles API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Profiles API - Example</p>
            <Code lang="python">{`# Get company profile
profile = client.profiles.profile("AAPL")
print(f"Company: {profile['name']}")
print(f"Industry: {profile['industry']}")
print(f"Sector: {profile['sector']}")

# Get key statistics
stats = client.profiles.statistics("AAPL")
print(f"Market Cap: {stats['market_cap']:,.0f}")
print(f"P/E Ratio: {stats['pe_ratio']}")

# Get upcoming events
calendar = client.profiles.calendar("AAPL")
for event in calendar:
    print(f"{event['date']}: {event['event_type']}")`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Financials API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Financials API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.financials
            </code>
            <p className="text-gray-700 mb-6">
              Comprehensive financial statement data and calculated metrics.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Key Methods</h3>
            <div className="grid grid-cols-1 md:grid-cols-1 gap-2">
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.revenue(ticker, periods)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.net_income(ticker, periods)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.total_assets(ticker, periods)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.total_liabilities(ticker, periods)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.stockholders_equity(ticker, periods)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.current_assets(ticker, periods)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.current_liabilities(ticker, periods)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.operating_cash_flow(ticker, periods)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.capital_expenditures(ticker, periods)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.free_cash_flow(ticker, periods)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.shares_outstanding_basic(ticker, periods)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.shares_outstanding_diluted(ticker, periods)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.balance_sheet(ticker, year, quarter)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.income_statement(ticker, year, quarter)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.cash_flow_statement(ticker, year, quarter)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.metrics(ticker)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.snapshot(ticker)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.dcf_value(ticker)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.dcf_rate(ticker)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.eps(ticker, from, to)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.pe(ticker, from, to, frame)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.market_cap(ticker, from, to, frame)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.roe(ticker, from, to)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.enterprise_value(ticker, from, to, frame)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.ebitda(ticker, from, to)</code></div>
              <div><code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">financials.debt_to_equity(ticker, from, to)</code></div>
            </div>
            <p className="text-sm text-gray-600 mt-2">Metric methods accept optional <code>periods</code> parameter. Statement methods accept optional <code>year</code> and <code>quarter</code>. Historical valuation methods accept optional <code>from</code>, <code>to</code>, and <code>frame</code> parameters.</p>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Financials API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Financials API - Example</p>
            <Code lang="python">{`# Get revenue history (last 4 quarters)
revenue = client.financials.revenue("AAPL", periods=4)
for period in revenue:
    print(f"{period['date']}: {period['value']:,.0f}")

# Get comprehensive financial snapshot
snapshot = client.financials.snapshot("AAPL")
print(f"Revenue (TTM): {snapshot['revenue_ttm']:,.0f}")
print(f"Gross Margin: {snapshot['gross_margin']}%")
print(f"Operating Margin: {snapshot['operating_margin']}%")
print(f"Debt/Equity: {snapshot['debt_to_equity']}")

# Calculate free cash flow trend
fcf = client.financials.free_cash_flow("AAPL", periods=5)
fcf_values = [period['value'] for period in fcf]
print(f"FCF Trend: {fcf_values}")

# DCF valuation
dcf = client.financials.dcf_value("AAPL")
print(f"Fair Price: {dcf['fair_price']:.2f}, Recommendation: {dcf['recommendation']}")

# Discount rate / WACC
rate = client.financials.dcf_rate("AAPL")
print(f"WACC: {rate['wacc'] * 100:.2f}%")`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Filings API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Filings API (SEC)</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.filings
            </code>
            <p className="text-gray-700 mb-6">
              Access SEC filings data for public companies.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">recent(ticker, limit, form)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get recent SEC filings for a company</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">history(ticker, form_type, start_date, end_date)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get specific form type filings by date range</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">list_forms()</td>
                    <td className="px-4 py-3 text-sm text-gray-700">List available SEC form types and descriptions</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">search(ticker, form, year, quarter)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Search filings by year/quarter and optional filters</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">document_sentiment(document_id)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get sentiment analysis of an SEC filing document by its base64 document ID</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">document_text(document_id)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get raw text content of an SEC filing document by its base64 document ID</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Filings API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Filings API - Example</p>
            <Code lang="python">{`# List all available form types
form_types = client.filings.list_forms()
print(form_types[:5])

# Get recent 10-K filings for Apple
filings = client.filings.recent("AAPL", form="10-K", limit=5)
for filing in filings:
    print(f"{filing['filed_date']}: {filing['form']}")

# Search for Q1 2024 filings
q1_filings = client.filings.search(
    form="10-Q",
    year="2024",
    quarter="Q1"
)

# Get specific 10-Q filing in Q1 2024
ten_q = client.filings.history(
    "AAPL",
    form_type="10-Q",
    start_date="2024-01-01",
    end_date="2024-03-31"
)`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Insiders API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Insiders API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.insiders
            </code>
            <p className="text-gray-700 mb-6">
              Access insider trading data, institutional ownership, and fund holdings.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">funds(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get fund ownership data</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">individuals(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get insider holders (individuals)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">institutions(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get institutional ownership data</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">ownership(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get major holders breakdown</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">activity(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get net share purchase activity</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">transactions(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get insider transactions</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Insiders API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Insiders API - Example</p>
            <Code lang="python">{`# Get institutional ownership
institutions = client.insiders.institutions("AAPL")
print("Top Institutional Holders:")
for holder in institutions[:5]:
    print(f"{holder['name']}: {holder['shares']:,} shares")

# Get insider transactions
transactions = client.insiders.transactions("AAPL")
print("\nRecent Insider Transactions:")
for tx in transactions[:5]:
    print(f"{tx['date']}: {tx['insider']} - {tx['transaction_type']}: {tx['shares']:,} shares")

# Get ownership breakdown
ownership = client.insiders.ownership("AAPL")
print(f"\nInsiders: {ownership['insider_percent']}%")
print(f"Institutions: {ownership['institution_percent']}%")
print(f"Retail: {ownership['retail_percent']}%")`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Earnings API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Earnings API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.earnings
            </code>
            <p className="text-gray-700 mb-6">
              Historical earnings data, trends, and estimates.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">history(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get historical earnings data</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">trend(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get earnings trend and estimates</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">index(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get index trend estimates</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">report(ticker, year, quarter)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get detailed earnings report for a specific period</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">transcript(ticker, year, quarter)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get earnings call transcript for a ticker, year, and quarter</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">transcript_sentiment(id)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get sentiment analysis of an earnings call transcript by its base64 ID</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Earnings API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Earnings API - Example</p>
            <Code lang="python">{`# Get earnings history
history = client.earnings.history("AAPL")
print("Earnings History:")
for period in history:
    print(f"{period['date']}: EPS {period['eps']} vs {period['estimate']} est")

# Get earnings trend
trend = client.earnings.trend("AAPL")
print(f"\nCurrent Quarter Estimate: {trend['current_quarter_estimate']}")
print(f"Next Quarter Estimate: {trend['next_quarter_estimate']}")
print(f"Current Year Estimate: {trend['current_year_estimate']}")

# Get specific quarterly report
q1_2024 = client.earnings.report("AAPL", year="2024", quarter="Q1")
print(f"\nQ1 2024 Revenue: {q1_2024['revenue']:,.0f}")
print(f"Q1 2024 EPS: {q1_2024['eps']}")`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Economic API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Economic API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.econ
            </code>
            <p className="text-gray-700 mb-6">
              Economic indicators, datasets, and calendar events.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">find(query)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Find economic series using natural language description</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">search(query)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Search for economic series</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">dataset(series_id)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get series observations</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">calendar(from_date, to_date, country, min_importance, currency, category)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get economic calendar with filters</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Economic API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Economic API - Example</p>
            <Code lang="python">{`# Search for inflation series
inflation_series = client.econ.search("inflation")
for series in inflation_series:
    print(f"{series['id']}: {series['name']}")

# Get GDP data
gdp = client.econ.dataset("GDP_USA")
print("\nGDP History:")
for observation in gdp['observations'][-5:]:
    print(f"{observation['date']}: {observation['value']}")

# Get important economic events
calendar = client.econ.calendar(
    from_date="2024-04-01",
    to_date="2024-04-30",
    country="US",
    min_importance=3
)
for event in calendar:
    print(f"{event['date']}: {event['event']} - {event['importance']}/3")`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Sentiment API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Sentiment API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.sentiment
            </code>
            <p className="text-gray-700 mb-6">
              Social media sentiment, news sentiment, and analyst sentiment data.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">social(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get social media sentiment</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">news(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get news sentiment</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">analyst(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get analyst sentiment</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Sentiment API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Sentiment API - Example</p>
            <Code lang="python">{`# Get social media sentiment
social = client.sentiment.social("TSLA")
print(f"\nSocial Media:")
print(f"Mentions: {social['mentions']}")
print(f"Positive: {social['positive_percent']}%")
print(f"Negative: {social['negative_percent']}%")

# Get analyst sentiment
analyst = client.sentiment.analyst("TSLA")
print(f"\nAnalyst Sentiment:")
print(f"Buy: {analyst['buy_count']}")
print(f"Hold: {analyst['hold_count']}")
print(f"Sell: {analyst['sell_count']}")`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- News API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">News API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.news
            </code>
            <p className="text-gray-700 mb-6">
              Financial news articles by company, country, or category.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">general()</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get general financial news</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">company(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get news for a specific company</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">country(country)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get news for a specific country</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">category(category)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get news by category</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="News API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">News API - Example</p>
            <Code lang="python">{`# Get top general headlines
headlines = client.news.general()
print("Top Financial News:")
for article in headlines[:5]:
    print(f"- {article['title']} ({article['source']})")

# Get company-specific news
aapl_news = client.news.company("AAPL")
print("\nApple News:")
for article in aapl_news[:3]:
    print(f"{article['published_at']}: {article['title']}")

# Get news by category
earnings_news = client.news.category("earnings")
mergers_news = client.news.category("mergers")`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Supply Chain API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Supply Chain API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.supply_chain
            </code>
            <p className="text-gray-700 mb-6">
              Company relationships including customers, suppliers, and industry peers.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">customers(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get major customers</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">suppliers(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get key suppliers</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">peers(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get industry peers</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Supply Chain API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Supply Chain API - Example</p>
            <Code lang="python">{`# Get Apple's suppliers
suppliers = client.supply_chain.suppliers("AAPL")
print("Apple Suppliers:")
for supplier in suppliers[:5]:
    print(f"- {supplier['name']} ({supplier['ticker']})")

# Get industry peers
peers = client.supply_chain.peers("AAPL")
print("\nIndustry Peers:")
for peer in peers:
    print(f"- {peer['name']} ({peer['ticker']})")

# Get major customers
customers = client.supply_chain.customers("TSLA")
print("\nTesla Customers:")
for customer in customers:
    print(f"- {customer['name']}")`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- ETFs API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">ETFs API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.etfs
            </code>
            <p className="text-gray-700 mb-6">
              Comprehensive ETF data including fund information, holdings, and exposure analysis.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">tickers(country, exchange)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get all ETF tickers with optional filtering</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get a single ETF ticker by its symbol</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">quote(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get a quote for an ETF by its symbol</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">prices(ticker, from_date, to_date, frame)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get historical prices for an ETF</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">fund(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get detailed fund data for an ETF</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">holdings(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get holdings data for an ETF</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">exposure(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get exposure data for an ETF holding</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">weights(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get weights data for an ETF's components</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">gainers(days, limit)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get top ETF gainers</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">losers(days, limit)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get top ETF losers</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="ETFs API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">ETFs API - Example</p>
            <Code lang="python">{`# Get SPY fund information
spy = client.etfs.fund("SPY")
print(f"SPY - {spy['name']}")
print(f"AUM: {spy['aum']:,.0f}")
print(f"Expense Ratio: {spy['expense_ratio']}%")
print(f"Inception Date: {spy['inception_date']}")

# Get top holdings
holdings = client.etfs.holdings("SPY")
print("\nTop Holdings:")
for holding in holdings[:5]:
    print(f"{holding['name']}: {holding['weight']}%")

# Get sector exposure
exposure = client.etfs.exposure("SPY")
print("\nSector Exposure:")
for sector, weight in exposure['sectors'].items():
    print(f"{sector}: {weight}%")`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Forex API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Forex API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.forex
            </code>
            <p className="text-gray-700 mb-6">
              Foreign exchange currency data including tickers, quotes, and historical prices.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">tickers(country, exchange)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get all forex tickers with optional filtering</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get a single forex ticker by its symbol</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">quote(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get current quote for a forex pair</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">prices(ticker, from_date, to_date, frame)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get historical prices for a forex pair</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">gainers(days, limit)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get top forex gainers</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">losers(days, limit)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get top forex losers</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Forex API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Forex API - Example</p>
            <Code lang="python">{`# Get all major forex pairs
pairs = client.forex.tickers()
print("Major Forex Pairs:")
for pair in pairs[:5]:
    print(f"- {pair['symbol']}: {pair['name']}")

# Get EUR/USD quote
eur_usd = client.forex.quote("EUR-USD")
print(f"\nEUR/USD: {eur_usd['price']}")
print(f"Change: {eur_usd['change']}%")
print(f"Day Range: {eur_usd['day_low']} - {eur_usd['day_high']}")

# Get historical USD/JPY prices
usd_jpy = client.forex.prices(
    "USD-JPY",
    from_date="2024-01-01",
    to_date="2024-03-31",
    frame="daily"
)`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Futures API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Futures API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.futures
            </code>
            <p className="text-gray-700 mb-6">
              Commodity and financial futures data including tickers, quotes, and historical prices.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">tickers(exchange)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get all futures tickers with optional filtering</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get a single futures ticker by its symbol</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">quote(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get current quote for a futures contract</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">prices(ticker, from_date, to_date, frame)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get historical prices for a futures contract</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">gainers(days, limit)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get top futures gainers</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">losers(days, limit)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get top futures losers</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Futures API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Futures API - Example</p>
            <Code lang="python">{`# Get all futures tickers
futures = client.futures.tickers()
print("Available Futures:")
for future in futures[:5]:
    print(f"- {future['symbol']}: {future['name']}")

# Get gold futures quote
gold = client.futures.quote("GC=F")
print(f"\nGold Futures: {gold['price']}")
print(f"Settlement: {gold['settlement']}")
print(f"Open Interest: {gold['open_interest']}")

# Get crude oil futures prices
oil_prices = client.futures.prices(
    "CL=F",
    from_date="2024-01-01",
    to_date="2024-03-31",
    frame="daily"
)`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Indices API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Indices API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.indices
            </code>
            <p className="text-gray-700 mb-6">
              Stock market indices data including tickers, quotes, and historical prices.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">tickers(exchange)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get all index tickers with optional filtering</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get a single index ticker by its symbol</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">quote(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get current quote for an index</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">prices(ticker, from_date, to_date, frame)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get historical prices for an index</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">gainers(days, limit)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get top index gainers</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">losers(days, limit)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get top index losers</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">components(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get index components for a given index</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">exposure(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get index exposure for a given index</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Indices API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Indices API - Example</p>
            <Code lang="python">{`# Get all indices
indices = client.indices.tickers()
print("Major Indices:")
for index in indices[:5]:
    print(f"- {index['symbol']}: {index['name']}")

# Get S&P 500 quote
spx = client.indices.quote("^GSPC")
print(f"\nS&P 500: {spx['price']:,.2f}")
print(f"Change: {spx['change']}%")
print(f"YTD Change: {spx['ytd_change']}%")

# Get NASDAQ historical prices
nasdaq = client.indices.prices(
    "^IXIC",
    from_date="2024-01-01",
    to_date="2024-03-31",
    frame="daily"
)`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Credit API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Credit API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.credit
            </code>
            <p className="text-gray-700 mb-6">
              Credit ratings and entity search for companies and financial instruments.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">search(query)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Search for credit entities</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">ratings(entity_id)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get ratings for a specific credit entity</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Credit API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Credit API - Example</p>
            <Code lang="python">{`# Search for credit entities
results = client.credit.search("Apple")
for entity in results:
    print(f"{entity['name']} - {entity['entity_type']}")

# Get credit ratings for Apple
ratings = client.credit.ratings("AAPL")
print(f"\nApple Credit Ratings:")
print(f"Moody's: {ratings['moodys']}")
print(f"S&P: {ratings['sp']}")
print(f"Fitch: {ratings['fitch']}")
print(f"Outlook: {ratings['outlook']}")`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- ESG API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">ESG API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.esg
            </code>
            <p className="text-gray-700 mb-6">
              Environmental, Social, and Governance (ESG) scores and metrics for publicly traded companies.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">data(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get comprehensive ESG data for a specific company</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="ESG API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">ESG API - Example</p>
            <Code lang="python">{`# Get ESG data for Microsoft
esg = client.esg.data("MSFT")
print(f"Overall ESG Score: {esg['score']}")
print(f"Environmental Score: {esg['environmental_score']}")
print(f"Environmental Grade: {esg['environmental_grade']}")
print(f"Social Score: {esg['social_score']}")
print(f"Social Grade: {esg['social_grade']}")
print(f"Governance Score: {esg['governance_score']}")
print(f"Governance Grade: {esg['governance_grade']}")

# Access detailed metrics
if 'controversies' in esg:
    print(f"\nControversies: {len(esg['controversies'])}")
for metric in esg['key_metrics']:
    print(f"{metric['name']}: {metric['value']}")`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Web Traffic API ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Web Traffic API</h2>
            <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded mb-4 inline-block break-all">
              client.web_traffic
            </code>
            <p className="text-gray-700 mb-6">
              Website traffic and analytics data for publicly traded companies.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Available Methods</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-800">traffic(ticker)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Get website traffic and analytics data</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Web Traffic API - Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Web Traffic API - Example</p>
            <Code lang="python">{`# Get web traffic data for Amazon
traffic = client.web_traffic.traffic("AMZN")
print(f"Monthly Visits: {traffic['monthly_visits']:,}")
print(f"Monthly Unique Visitors: {traffic['monthly_unique_visitors']:,}")
print(f"Pages per Visit: {traffic['pages_per_visit']}")
print(f"Average Visit Duration: {traffic['avg_visit_duration']}s")
print(f"Bounce Rate: {traffic['bounce_rate']}%")

# Traffic trends
print("\nTraffic by Source:")
for source, percentage in traffic['traffic_sources'].items():
    print(f"{source}: {percentage}%")

# Geographic distribution
print("\nTop Countries:")
for country in traffic['top_countries'][:5]:
    print(f"{country['name']}: {country['percentage']}%")`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Error Handling ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Error Handling</h2>
            <p className="text-gray-700 mb-6">
              The SDK raises descriptive exceptions that can be caught and handled gracefully.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Common Exceptions</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Error Pattern</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr><td className="px-4 py-3 text-sm font-mono">HTTP Error 4xx</td><td className="px-4 py-3 text-sm">Client error (invalid request, unauthorized, etc.)</td></tr>
                  <tr><td className="px-4 py-3 text-sm font-mono">HTTP Error 5xx</td><td className="px-4 py-3 text-sm">Server error (API temporarily unavailable)</td></tr>
                  <tr><td className="px-4 py-3 text-sm font-mono">Connection Error</td><td className="px-4 py-3 text-sm">Network failure or DNS resolution error</td></tr>
                  <tr><td className="px-4 py-3 text-sm font-mono">Timeout Error</td><td className="px-4 py-3 text-sm">Request exceeded timeout limit</td></tr>
                  <tr><td className="px-4 py-3 text-sm font-mono">Authentication Error</td><td className="px-4 py-3 text-sm">Missing or invalid API key</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Error handling with retry" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Error handling with retry</p>
            <Code lang="python">{`import time
from axion import Axion

client = Axion(api_key="your_api_key_here")

def fetch_with_retry(func, *args, max_retries=3, base_delay=1):
    """Fetch data with exponential backoff retry logic."""
    for attempt in range(max_retries):
        try:
            return func(*args)
        except Exception as e:
            error_str = str(e)

            # Handle rate limiting (429)
            if "429" in error_str and attempt < max_retries - 1:
                delay = base_delay * (2 ** attempt)
                print(f"Rate limited. Retrying in {delay}s...")
                time.sleep(delay)
                continue

            # Handle authentication errors
            elif "Authentication" in error_str or "401" in error_str:
                print("Invalid API key. Please check your credentials.")
                break

            # Handle not found (404)
            elif "404" in error_str:
                print(f"Resource not found: {args}")
                break

            # Handle server errors (5xx)
            elif "500" in error_str or "502" in error_str or "503" in error_str:
                if attempt < max_retries - 1:
                    delay = base_delay * (2 ** attempt)
                    print(f"Server error. Retrying in {delay}s...")
                    time.sleep(delay)
                    continue
                else:
                    print("API server unavailable. Please try again later.")

            # Re-raise unexpected errors
            else:
                raise

    return None

# Usage
data = fetch_with_retry(client.stocks.quote, "AAPL")
if data:
    print(f"Price: {data['price']}")`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Data Normalization ---------- */}
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
        <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Data Normalization</h2>
            <p className="text-gray-700 mb-6">
              All responses are automatically normalized: string numbers → int/float, "true"/"false" → bool, recursively.
              This means you can use the returned data directly without manual type conversion.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="border-l-4 border-blue-500 pl-4">
              <p className="font-medium text-gray-900 mb-1">String → Number</p>
              <p className="text-xs text-gray-600">"150.42" → 150.42 (float), "42" → 42 (int)</p>
            </div>
            <div className="border-l-4 border-blue-500 pl-4">
              <p className="font-medium text-gray-900 mb-1">String → Boolean</p>
              <p className="text-xs text-gray-600">"true"/"false" → True/False (case-insensitive)</p>
            </div>
            <div className="border-l-4 border-blue-500 pl-4">
              <p className="font-medium text-gray-900 mb-1">Deep recursion</p>
              <p className="text-xs text-gray-600">Nested dictionaries and lists are traversed and normalized</p>
            </div>
            <div className="border-l-4 border-blue-500 pl-4">
              <p className="font-medium text-gray-900 mb-1">Null handling</p>
              <p className="text-xs text-gray-600">"null", "None" remain as None/null values</p>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Automatic type conversion" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Automatic type conversion</p>
            <Code lang="python">{`# Raw API returns:
# {
#   "price": "150.42",
#   "volume": "12345678",
#   "active": "true",
#   "pe_ratio": "25.6",
#   "details": {
#     "has_dividend": "false",
#     "dividend_yield": "0.5"
#   }
# }

# After normalization:
# {
#   "price": 150.42,
#   "volume": 12345678,
#   "active": True,
#   "pe_ratio": 25.6,
#   "details": {
#     "has_dividend": False,
#     "dividend_yield": 0.5
#   }
# }

# Use directly without casting:
data = client.stocks.quote("AAPL")
price = data['price']        # Already a float
volume = data['volume']      # Already an int
if data['active']:           # Already a bool
    print(f"Trading active with P/E {data['pe_ratio']}")`}</Code>
          </div>
        </div>
      </div>

      {/* ---------- Complete Example ---------- */}
      <div>
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Complete Example</h2>
          <p className="text-gray-700 max-w-[900px] mb-6">
            A comprehensive script demonstrating multiple API calls, data analysis with pandas, and visualization.
          </p>
        </div>
        <Code lang="python">{`#!/usr/bin/env python3
"""
AxionQuant SDK Complete Example
Demonstrates multiple API endpoints and data analysis techniques
"""
from axion import Axion
import pandas as pd
import matplotlib.pyplot as plt
import seaborn as sns
from datetime import datetime, timedelta
import time
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

class CompanyAnalyzer:
    def __init__(self, api_key):
        self.client = Axion(api_key=api_key)

    def analyze_company(self, ticker):
        """Fetch and display comprehensive company data."""
        print(f"\\n{'='*60}")
        print(f"COMPANY ANALYSIS: {ticker}")
        print('='*60)

        try:
            # Profile information
            profile = self.client.profiles.profile(ticker)
            print(f"\\n PROFILE:")
            print(f"  Name: {profile.get('name')}")
            print(f"  Sector: {profile.get('sector')}")
            print(f"  Industry: {profile.get('industry')}")
            print(f"  Employees: {profile.get('full_time_employees'):,}")

            # Stock quote
            quote = self.client.stocks.quote(ticker)
            print(f"\\n MARKET DATA:")
            print(f"  Price: {quote.get('price'):,.2f}")
            print(f"  Change: {quote.get('change')}%")
            print(f"  Volume: {quote.get('volume'):,}")
            print(f"  Market Cap: {quote.get('market_cap'):,.0f}")

            # ESG data
            esg = self.client.esg.data(ticker)
            print(f"\\n ESG SCORES:")
            print(f"  Overall: {esg.get('score')}")
            print(f"  Environmental: {esg.get('environmental_grade')}")
            print(f"  Social: {esg.get('social_grade')}")
            print(f"  Governance: {esg.get('governance_grade')}")

            # Financial snapshot
            snapshot = self.client.financials.snapshot(ticker)
            print(f"\\n FINANCIALS:")
            print(f"  Revenue (TTM): {snapshot.get('revenue_ttm'):,.0f}")
            print(f"  Gross Margin: {snapshot.get('gross_margin')}%")
            print(f"  Operating Margin: {snapshot.get('operating_margin')}%")
            print(f"  Debt/Equity: {snapshot.get('debt_to_equity')}")

            # Sentiment
            sentiment = self.client.sentiment.social(ticker)
            print(f"\n SENTIMENT:")
            print(f"  Score: {sentiment.get('score')}")
            print(f"  Label: {sentiment.get('label')}")

            # Recent news
            news = self.client.news.company(ticker)
            print(f"\\n RECENT NEWS (Top 3):")
            for article in news[:3]:
                print(f"  • {article.get('title')}")
                print(f"    {article.get('published_at')} - {article.get('source')}")

            return {
                'ticker': ticker,
                'price': quote.get('price'),
                'market_cap': quote.get('market_cap'),
                'esg_score': esg.get('score'),
                'sentiment_score': sentiment.get('score'),
                'revenue_ttm': snapshot.get('revenue_ttm'),
                'gross_margin': snapshot.get('gross_margin')
            }

        except Exception as e:
            print(f"Error analyzing {ticker}: {e}")
            return None

    def analyze_portfolio(self, tickers):
        """Analyze multiple companies and create comparison."""
        results = []
        for ticker in tickers:
            result = self.analyze_company(ticker)
            if result:
                results.append(result)
            time.sleep(1)  # Rate limiting

        if results:
            df = pd.DataFrame(results)

            print("\\n" + "="*60)
            print("PORTFOLIO COMPARISON")
            print("="*60)
            print(df.to_string(index=False))

            # Create visualizations
            fig, axes = plt.subplots(2, 2, figsize=(14, 10))
            fig.suptitle('Portfolio Analysis', fontsize=16)

            # Market Cap comparison
            axes[0, 0].barh(df['ticker'], df['market_cap'])
            axes[0, 0].set_xlabel('Market Cap ($B)')
            axes[0, 0].set_title('Market Capitalization')

            # ESG Scores
            axes[0, 1].bar(df['ticker'], df['esg_score'], color='green')
            axes[0, 1].set_ylabel('ESG Score')
            axes[0, 1].set_title('ESG Scores')

            # Sentiment
            axes[1, 0].bar(df['ticker'], df['sentiment_score'], color='blue')
            axes[1, 0].set_ylabel('Sentiment Score')
            axes[1, 0].set_title('Sentiment Analysis')

            # Margins
            x = range(len(df))
            width = 0.35
            axes[1, 1].bar([i - width/2 for i in x], df['gross_margin'], width, label='Gross Margin', color='orange')
            axes[1, 1].set_xticks(x)
            axes[1, 1].set_xticklabels(df['ticker'])
            axes[1, 1].set_ylabel('Margin %')
            axes[1, 1].set_title('Profit Margins')
            axes[1, 1].legend()

            plt.tight_layout()
            plt.show()

            return df
        return None

    def historical_analysis(self, ticker, months=6):
        """Analyze historical price trends."""
        end_date = datetime.now()
        start_date = end_date - timedelta(days=30*months)

        print(f"\\n HISTORICAL ANALYSIS: {ticker}")
        print(f"Period: {start_date.strftime('%Y-%m-%d')} to {end_date.strftime('%Y-%m-%d')}")

        # Get historical prices
        prices = self.client.stocks.prices(
            ticker,
            from_date=start_date.strftime('%Y-%m-%d'),
            to_date=end_date.strftime('%Y-%m-%d'),
            frame='daily'
        )

        if prices:
            df = pd.DataFrame(prices)
            df['date'] = pd.to_datetime(df['date'])
            df.set_index('date', inplace=True)

            # Calculate metrics
            df['daily_return'] = df['close'].pct_change() * 100
            df['sma_20'] = df['close'].rolling(window=20).mean()
            df['sma_50'] = df['close'].rolling(window=50).mean()

            # Print statistics
            print(f"\\n STATISTICS:")
            print(f"  Start Price: {df['close'].iloc[0]:.2f}")
            print(f"  End Price: {df['close'].iloc[-1]:.2f}")
            print(f"  Total Return: {((df['close'].iloc[-1] / df['close'].iloc[0]) - 1) * 100:.2f}%")
            print(f"  Max Price: {df['close'].max():.2f}")
            print(f"  Min Price: {df['close'].min():.2f}")
            print(f"  Volatility (daily): {df['daily_return'].std():.2f}%")

            # Plot
            fig, (ax1, ax2) = plt.subplots(2, 1, figsize=(12, 8))

            # Price chart with moving averages
            ax1.plot(df.index, df['close'], label='Close Price', linewidth=1)
            ax1.plot(df.index, df['sma_20'], label='20-day SMA', linestyle='--', alpha=0.7)
            ax1.plot(df.index, df['sma_50'], label='50-day SMA', linestyle='--', alpha=0.7)
            ax1.set_ylabel('Price ($)')
            ax1.set_title(f'{ticker} - Historical Prices')
            ax1.legend()
            ax1.grid(True, alpha=0.3)

            # Daily returns histogram
            ax2.hist(df['daily_return'].dropna(), bins=50, edgecolor='black', alpha=0.7)
            ax2.set_xlabel('Daily Return (%)')
            ax2.set_ylabel('Frequency')
            ax2.set_title('Distribution of Daily Returns')
            ax2.grid(True, alpha=0.3)

            plt.tight_layout()
            plt.show()

            return df
        return None

def main():
    # Initialize
    analyzer = CompanyAnalyzer(api_key="your_api_key_here")

    # Analyze single company
    analyzer.analyze_company("AAPL")

    # Analyze portfolio
    portfolio = ["AAPL", "MSFT", "GOOGL", "AMZN", "TSLA"]
    analyzer.analyze_portfolio(portfolio)

    # Historical analysis
    analyzer.historical_analysis("AAPL", months=6)

if __name__ == "__main__":
    main()`}</Code>
      </div>


      {/* ---------- Additional Resources ---------- */}
      <div className="mt-8 p-6 bg-gray-50 border border-gray-200 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Resources</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a href="https://github.com/axionquant/python-sdk" target="_blank" rel="noopener noreferrer" className="block p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
            <h4 className="font-medium text-gray-900 mb-1">GitHub Repository</h4>
            <p className="text-sm text-gray-600">Source code, issues, and contributions</p>
          </a>
          <a href="https://pypi.org/project/axionquant-sdk/" target="_blank" rel="noopener noreferrer" className="block p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
            <h4 className="font-medium text-gray-900 mb-1">PyPI Package</h4>
            <p className="text-sm text-gray-600">axionquant-sdk on Python Package Index</p>
          </a>
          <a href="https://docs.axionquant.com" target="_blank" rel="noopener noreferrer" className="block p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
            <h4 className="font-medium text-gray-900 mb-1">API Documentation</h4>
            <p className="text-sm text-gray-600">Complete API reference and guides</p>
          </a>
        </div>
      </div>
    </div>
    </div>
  );
}
